import {LoginCallback, useOktaAuth} from '@okta/okta-react';
import PropTypes from 'prop-types';
import React, {useEffect, useRef} from 'react';
import {useNavigate} from 'react-router-dom';

import log from 'loglevel';

const OktaLoginCallback = () => {
  const {authState, oktaAuth} = useOktaAuth();
  const navigate = useNavigate();

  const isLoginCallbackRef = useRef(oktaAuth.isLoginRedirect());
  const isLoginCallback = isLoginCallbackRef.current;

  useEffect(() => {
    // Only redirect home when the user landed on /oktalogin directly while
    // already authenticated (not during an active callback).
    if (authState?.isAuthenticated && !isLoginCallback) {
      navigate('/', {replace: true});
    }
  }, [authState?.isAuthenticated, isLoginCallback, navigate]);

  if (authState?.isAuthenticated && !isLoginCallback) {
    return null;
  }

  // Custom error handler for OAuth/Okta errors
  const onAuthResume = async () => {
    try {
      const searchParams = new URLSearchParams(globalThis.location.search);
      const error = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      if (error) {
        log.error('OAuth Error during login callback:', {
          error,
          errorDescription,
        });

        await oktaAuth.signInWithRedirect();
        return;
      }
    } catch (err) {
      log.error('Error handling OAuth callback:', err);
    }
  };

  // Error component to display when LoginCallback encounters an error
  const ErrorComponent = ({error}) => {
    useEffect(() => {
      log.error('Okta LoginCallback error:', error);

      const handleError = async () => {
        try {
          await oktaAuth.signInWithRedirect();
        } catch (redirectError) {
          log.error('Error redirecting to login:', redirectError);

          navigate('/', {replace: true});
        }
      };

      handleError();
    }, [error, navigate, oktaAuth]);

    return null;
  };

  ErrorComponent.propTypes = {
    error: PropTypes.object,
  };

  return (
    <LoginCallback
      errorComponent={ErrorComponent}
      onAuthResume={onAuthResume}
    />
  );
};

export default OktaLoginCallback;
